import React, { useEffect, useRef, useState, useCallback } from "react";
import { Form, Button, Row, Col, Spinner, Table, Alert } from "react-bootstrap";
import SchemeSearch from "./searchMedicalAid";
import PlanOptionSelector from "./searchMedicalAidOptionPlan";
import { checkFamilyEligibility } from "../../mediKreditapi";
import { jwtDecode } from "jwt-decode";
import { getUserPracticeById } from "../../api";

const extractDigits = (value) => value?.match(/\d{6}$/)?.[0] || "";
// Search always prefers mediKreditPlanNumber04; falls back to 03 then 06 only
// when 04 is absent.
const pickPlan030406 = (p03, p04, p06) => p04 || p03 || p06 || "";
const normalize030406 = (p03, p04, p06) => {
  const mk03 = extractDigits(p03);
  const mk04 = extractDigits(p04);
  const mk06 = extractDigits(p06);

  const n03 = mk03 || mk04 || mk06 || "";
  const n04 = mk04 || (mk03 ? "" : mk06) || "";
  const n06 = mk06 || "";

  return {
    mediKreditPlanNumber03: n03,
    mediKreditPlanNumber04: n04,
    mediKreditPlanNumber06: n06,
    plan: mk04 || mk03 || mk06 || "",
  };
};

export default function SearchMedAidPatient({
  onDependantSelect,
  addPatientSteps,
  patientAccount,
  accountExisting,
  onDependantListUpdate,
  iodPatient,
  editIodPatient,
  editPatient,
}) {
  const [loading, setLoading] = useState(false);
  const [practiceInfo, setPracticeInfo] = useState({
    sp_bhf: "",
    sp_hpc: "",
    grp_prac: "",
  });
  const [warningMsg, setWarningMsg] = useState("");
  
  // ✅ NEW: Track if IOD was successfully committed
  const [iodCommitted, setIodCommitted] = useState(false);

  const [iodPlanSelection, setIodPlanSelection] = useState({
    selectedId: "",
    iodFund: editPatient?.patientIODFund || "",
    iodFundCode: editPatient?.patientIODFundCode || "",
    iodPlan: editPatient?.patientIODPlan || "",
    iodPlanCode: editPatient?.patientIODPlanCode || "",
    iodOption: editPatient?.patientIODOption || "",
    iodOptionCode: editPatient?.patientIODOptionCode || "",
  });

  const [iodCase, setIodCase] = useState({
    iodEmployer: editPatient?.patientIODEmployer || "",
    employerRegistrationNumber: editPatient?.patientIODEmployerWCARegNum || "",
    dateOfInjury: editPatient?.patientIODDateOfInjury || "",
    claimRefNumber: editPatient?.patientIODClaimRefNumber || "",
  });

  const [medicalAidInfo, setMedicalAidInfo] = useState({
    medicalAid: editPatient?.patientMedicalAid || (accountExisting ? patientAccount?.accountMedicalAid || "" : ""),
    medicalAidCode: editPatient?.patientMedicalAidCode || (accountExisting ? patientAccount?.accountMedicalAidCode || "" : ""),
    ch_id: editPatient?.patientMedicalAidNumber || (accountExisting ? patientAccount?.accountMedicalAidNumber || "" : ""),
    medicalAidPlan: editPatient?.patientMedicalAidPlan || (accountExisting ? patientAccount?.accountMedicalAidPlan || "" : ""),
    medicalAidPlanCode: editPatient?.patientMedicalAidPlanCode || (accountExisting ? patientAccount?.accountMedicalAidPlanCode || "" : ""),
    medicalAidOption: editPatient?.patientMedicalAidOption || (accountExisting ? patientAccount?.accountMedicalAidOption || "" : ""),
    medicalAidOptionCode: editPatient?.patientMedicalAidOptionCode || (accountExisting ? patientAccount?.accountMedicalAidOptionCode || "" : ""),
    mediKreditPlanNumber03: editPatient?.patientMediKreditPlanNumber03 || (accountExisting ? patientAccount?.accountMediKreditPlanNumber03 || "" : ""),
    mediKreditPlanNumber04: editPatient?.patientMediKreditPlanNumber04 || (accountExisting ? patientAccount?.accountMediKreditPlanNumber04 || "" : ""),
    mediKreditPlanNumber06: editPatient?.patientMediKreditPlanNumber06 || (accountExisting ? patientAccount?.accountMediKreditPlanNumber06 || "" : ""),
    plan: editPatient?.patientMedicalAidPlanCode
      ? pickPlan030406(
          editPatient.patientMediKreditPlanNumber03,
          editPatient.patientMediKreditPlanNumber04,
          editPatient.patientMediKreditPlanNumber06
        ) || ""
      : accountExisting
      ? pickPlan030406(
          patientAccount?.accountMediKreditPlanNumber03,
          patientAccount?.accountMediKreditPlanNumber04,
          patientAccount?.accountMediKreditPlanNumber06
        ) || ""
      : "",
    lastCheck: editPatient?.patientMedicalAidLastCheck || (accountExisting ? patientAccount?.accountLastCheck || "" : ""),
    iodFund: "",
    iodFundCode: "",
    errorMessage: false,
  });

  const [mainPlanSelection, setMainPlanSelection] = useState({
    selectedId: editPatient?.patientMedicalAidPlanCode && editPatient?.patientMedicalAidOptionCode
      ? `${editPatient.patientMedicalAidPlanCode}-${editPatient.patientMedicalAidOptionCode}`
      : accountExisting && patientAccount?.accountMedicalAidPlanCode && patientAccount?.accountMedicalAidOptionCode
      ? `${patientAccount.accountMedicalAidPlanCode}-${patientAccount.accountMedicalAidOptionCode}`
      : "",
    medicalAidPlan: editPatient?.patientMedicalAidPlan || (accountExisting ? patientAccount?.accountMedicalAidPlan || "" : ""),
    medicalAidPlanCode: editPatient?.patientMedicalAidPlanCode || (accountExisting ? patientAccount?.accountMedicalAidPlanCode || "" : ""),
    medicalAidOption: editPatient?.patientMedicalAidOption || (accountExisting ? patientAccount?.accountMedicalAidOption || "" : ""),
    medicalAidOptionCode: editPatient?.patientMedicalAidOptionCode || (accountExisting ? patientAccount?.accountMedicalAidOptionCode || "" : ""),
    mediKreditPlanNumber03: editPatient?.patientMediKreditPlanNumber03 || (accountExisting ? patientAccount?.accountMediKreditPlanNumber03 || "" : ""),
    mediKreditPlanNumber04: editPatient?.patientMediKreditPlanNumber04 || (accountExisting ? patientAccount?.accountMediKreditPlanNumber04 || "" : ""),
    mediKreditPlanNumber06: editPatient?.patientMediKreditPlanNumber06 || (accountExisting ? patientAccount?.accountMediKreditPlanNumber06 || "" : ""),
    plan: editPatient?.patientMedicalAidPlanCode
      ? pickPlan030406(
          editPatient.patientMediKreditPlanNumber03,
          editPatient.patientMediKreditPlanNumber04,
          editPatient.patientMediKreditPlanNumber06
        ) || ""
      : accountExisting
      ? pickPlan030406(
          patientAccount?.accountMediKreditPlanNumber03,
          patientAccount?.accountMediKreditPlanNumber04,
          patientAccount?.accountMediKreditPlanNumber06
        ) || ""
      : "",
  });

  const [dependants, setDependants] = useState([]);
  const [statusInfo, setStatusInfo] = useState(null);
  const [mainMemberCode, setMainMemberCode] = useState("");
  const [importDepCode, setImportDepCode] = useState("");
  const [depPlanSelections, setDepPlanSelections] = useState({});
  const [lastFamCheckPayload, setLastFamCheckPayload] = useState(null);
  const [iodErrors, setIodErrors] = useState({});
  const [manualApplied, setManualApplied] = useState(false);

  const validateIod = useCallback(() => {
    const errs = {};
    const claimRef = (iodCase?.claimRefNumber || "").trim();
    const doi = (iodCase?.dateOfInjury || "").trim();
    const employer = (iodCase?.iodEmployer || "").trim();
    const wca = (iodCase?.employerRegistrationNumber || "").trim();

    if (!claimRef) errs.claimRefNumber = "Claim Ref # is required.";
    if (!doi) errs.dateOfInjury = "Date of Injury is required.";
    if (!employer) errs.iodEmployer = "Employer is required.";
    if (!wca)
      errs.employerRegistrationNumber = "Employer WCA Reg # is required.";

    if (!medicalAidInfo?.iodFundCode)
      errs.iodFundCode = "Please select an IOD Fund.";
    if (!iodPlanSelection?.iodPlanCode || !iodPlanSelection?.iodOptionCode)
      errs.iodPlan = "Please select a Plan Option & Plan.";

    setIodErrors(errs);
    return Object.keys(errs).length === 0;
  }, [
    iodCase?.claimRefNumber,
    iodCase?.dateOfInjury,
    iodCase?.iodEmployer,
    iodCase?.employerRegistrationNumber,
    medicalAidInfo?.iodFundCode,
    iodPlanSelection?.iodPlanCode,
    iodPlanSelection?.iodOptionCode,
  ]);

  const isIOD = !!iodPatient;

  const detectMainMemberCode = (list) => {
    // Spec: main member is ALWAYS dependants[0]
    if (!list || list.length === 0) return "";
    return String(list[0].dependantCode || "");
  };

  const applyMainMemberSelection = useCallback((code) => {
    const mmCode = String(code || "");
    setImportDepCode("");
    setMainMemberCode(mmCode);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) return;

        const user = jwtDecode(token);
        const practices = await getUserPracticeById(user._id);

        const p0 = practices?.[0];
        if (!p0) return;

        setPracticeInfo({
          sp_bhf: p0.practiceNumber,
          sp_hpc: p0.hpcsaNumber,
          grp_prac: p0.practiceNumber,
        });
      } catch (e) {
        console.error("❌ Error loading practice info:", e);
      }
    })();
  }, []);

  const handleSchemeSelect = useCallback(
    (scheme) => {
      // Only block if adding to existing account (not in edit mode)
      if (accountExisting && !editPatient && !isIOD) return;

      // ✅ Reset committed state when changing scheme
      setIodCommitted(false);

      setMedicalAidInfo((prev) => {
        if (isIOD) {
          return {
            ...prev,
            iodFund: scheme?.name || "",
            iodFundCode: scheme?.code || "",
            medicalAid: "",
            medicalAidCode: "",
            ch_id: "",
            lastCheck: new Date().toISOString(),
          };
        }

        return {
          ...prev,
          medicalAid: scheme?.name || "",
          medicalAidCode: scheme?.code || "",
          iodFund: "",
          iodFundCode: "",
          lastCheck: new Date().toISOString(),
        };
      });
    },
    [accountExisting, editPatient, isIOD]
  );

  const handleNumberChange = useCallback(
    (e) => {
      // Only block if adding to existing account (not in edit mode)
      if (accountExisting && !editPatient) return;
      setMedicalAidInfo((prev) => ({
        ...prev,
        ch_id: e.target.value,
        lastCheck: new Date().toISOString(),
      }));
    },
    [accountExisting, editPatient]
  );

  const handleChangeIodCase = useCallback((e) => {
    const { name, value } = e.target;
    // ✅ Reset committed state when editing
    setIodCommitted(false);
    setIodCase((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  useEffect(() => {
    if (!accountExisting) return;
    if (!patientAccount) return;

    setMedicalAidInfo((prev) => ({
      ...prev,
      medicalAid: patientAccount.accountMedicalAid || "",
      medicalAidCode: patientAccount.accountMedicalAidCode || "",
      ch_id: patientAccount.accountMedicalAidNumber || "",
      medicalAidPlan: patientAccount.accountMedicalAidPlan || "",
      medicalAidPlanCode: patientAccount.accountMedicalAidPlanCode || "",
      medicalAidOption: patientAccount.accountMedicalAidOption || "",
      medicalAidOptionCode: patientAccount.accountMedicalAidOptionCode || "",
      mediKreditPlanNumber03:
        patientAccount.accountMediKreditPlanNumber03 || "",
      mediKreditPlanNumber04:
        patientAccount.accountMediKreditPlanNumber04 || "",
      mediKreditPlanNumber06:
        patientAccount.accountMediKreditPlanNumber06 || "",
      plan:
        pickPlan030406(
          patientAccount.accountMediKreditPlanNumber03,
          patientAccount.accountMediKreditPlanNumber04,
          patientAccount.accountMediKreditPlanNumber06
        ) || "",
    }));
  }, [accountExisting, patientAccount]);

  useEffect(() => {
    if (!isIOD || !editPatient) return;

    setMedicalAidInfo((prev) => ({
      ...prev,
      iodFund: editPatient?.patientIODFund || "",
      iodFundCode: editPatient?.patientIODFundCode || "",
    }));

    const iodOptionCode = editPatient?.patientIODOptionCode || "";
    const iodPlanCode = editPatient?.patientIODPlanCode || "";

    if (iodOptionCode && iodPlanCode) {
      setTimeout(() => {
        const selectedId = `${iodPlanCode}-${iodOptionCode}`;

        setIodPlanSelection((prev) => ({
          ...prev,
          selectedId,
          iodPlan: editPatient?.patientIODPlan || "",
          iodPlanCode: editPatient?.patientIODPlanCode || "",
          iodOption: editPatient?.patientIODOption || "",
          iodOptionCode: editPatient?.patientIODOptionCode || "",
        }));
      }, 300);
    }
  }, [isIOD, editPatient]);

  async function famCheck() {
    setWarningMsg("");

    if (!medicalAidInfo.medicalAidCode) {
      setWarningMsg("Please select a Medical Aid first.");
      return;
    }
    if (!medicalAidInfo.ch_id) {
      setWarningMsg("Please enter the Medical Aid Number.");
      return;
    }

    // Check if we have plan/option from new selection, existing patient, or existing account
    const hasPlanOption = mainPlanSelection?.selectedId ||
      (editPatient && medicalAidInfo.medicalAidPlanCode && medicalAidInfo.medicalAidOptionCode) ||
      (accountExisting && medicalAidInfo.medicalAidPlanCode && medicalAidInfo.medicalAidOptionCode);

    if (!hasPlanOption) {
      setWarningMsg("Please select a Plan Option & Plan before searching.");
      return;
    }

    setLoading(true);

    try {
      const payloadForCheck = {
        ...medicalAidInfo,
        medicalAidPlan: mainPlanSelection.medicalAidPlan || medicalAidInfo.medicalAidPlan,
        medicalAidPlanCode: mainPlanSelection.medicalAidPlanCode || medicalAidInfo.medicalAidPlanCode,
        medicalAidOption: mainPlanSelection.medicalAidOption || medicalAidInfo.medicalAidOption,
        medicalAidOptionCode: mainPlanSelection.medicalAidOptionCode || medicalAidInfo.medicalAidOptionCode,
        mediKreditPlanNumber03: mainPlanSelection.mediKreditPlanNumber03 || medicalAidInfo.mediKreditPlanNumber03,
        mediKreditPlanNumber04: mainPlanSelection.mediKreditPlanNumber04 || medicalAidInfo.mediKreditPlanNumber04,
        mediKreditPlanNumber06: mainPlanSelection.mediKreditPlanNumber06 || medicalAidInfo.mediKreditPlanNumber06,
        plan: mainPlanSelection.plan || medicalAidInfo.plan,
        lastCheck: new Date().toISOString(),
        errorMessage: false,
      };

      setLastFamCheckPayload(payloadForCheck);

      const response = await checkFamilyEligibility(
        practiceInfo,
        payloadForCheck
      );

      setStatusInfo({
        auths: response.auths || null,
        txNumber: response.txNumber || null,
        resultCode: response.resultCode || null,
        warnings: response.warnings || [],
        rejections: response.rejections || [],
      });

      const list = Array.isArray(response.dependants)
        ? response.dependants
        : [];
      setDependants(list);

      if (typeof onDependantListUpdate === "function") {
        onDependantListUpdate(list);
      }

      const detected = detectMainMemberCode(list);

      const fallback =
        detected ||
        (list.find((d) => String(d.dependantCode) === "0")?.dependantCode
          ? "0"
          : list[0]?.dependantCode
          ? String(list[0].dependantCode)
          : "");

      applyMainMemberSelection(fallback);
      setImportDepCode("");

      setMedicalAidInfo((prev) => ({
        ...prev,
        ...payloadForCheck,
        lastCheck: new Date().toISOString(),
        errorMessage: false,
      }));
    } catch (error) {
      console.error("⛔ Family Check Error:", error);

      const data = error?.response?.data || {};

      setStatusInfo({
        auths: data.auths || null,
        txNumber: data.txNumber || null,
        resultCode: data.resultCode || null,
        warnings: data.warnings || [],
        rejections: data.rejections || [],
      });

      setDependants([]);

      setMedicalAidInfo((prev) => ({
        ...prev,
        errorMessage: true,
        lastCheck: new Date().toISOString(),
      }));
    } finally {
      setLoading(false);
    }
  }

  const handleImportSelect = useCallback(
    (depCode) => {
      const code = String(depCode);
      setWarningMsg("");

      if (importDepCode === code) {
        setImportDepCode("");
        return;
      }

      const depSel = depPlanSelections?.[code];
      const depHasPlan = Boolean(depSel?.selectedId);

      if (!depHasPlan) {
        setWarningMsg(
          "Please select a Plan Option & Plan for the dependant you want to import."
        );
        return;
      }

      const mmCode = String(mainMemberCode || "");
      const importingIsMainMember = mmCode && mmCode === code;

      if (mmCode && !importingIsMainMember) {
        const mmSel = depPlanSelections?.[mmCode];
        const mmHasPlan = Boolean(mmSel?.selectedId);

        if (!mmHasPlan) {
          setWarningMsg(
            "Please select a Plan Option & Plan for the Main Member before importing a dependant."
          );
          return;
        }
      }

      setImportDepCode(code);
    },
    [importDepCode, depPlanSelections, mainMemberCode, setWarningMsg]
  );

  const handleMainMemberSelect = useCallback(
    (depCode) => {
      applyMainMemberSelection(
        depCode,
        dependants,
        lastFamCheckPayload || medicalAidInfo
      );
    },
    [applyMainMemberSelection, dependants, lastFamCheckPayload, medicalAidInfo]
  );

  const buildMedAidPayloadForDep = useCallback(
    (depCode, depObj) => {
      const code = String(depCode);
      const selected = depPlanSelections[code] || {};

      if (isIOD) {
        return {
          ...medicalAidInfo,
          iodFund: selected.iodFund || medicalAidInfo.iodFund || "",
          iodFundCode: selected.iodFundCode || medicalAidInfo.iodFundCode || "",
          iodPlan: selected.iodPlan || medicalAidInfo.iodPlan || "",
          iodPlanCode: selected.iodPlanCode || medicalAidInfo.iodPlanCode || "",
          iodOption: selected.iodOption || medicalAidInfo.iodOption || "",
          iodOptionCode:
            selected.iodOptionCode || medicalAidInfo.iodOptionCode || "",
          iodPatient: true,
          iodEmployer: depObj?.iodEmployer || "",
          iodEmployerRegistrationNumber:
            depObj?.iodEmployerRegistrationNumber || "",
          iodDateOfInjury: depObj?.iodDateOfInjury || "",
          iodClaimRefNumber: depObj?.iodClaimRefNumber || "",
          lastCheck: new Date().toISOString(),
        };
      }

      const mk = normalize030406(
        selected.mediKreditPlanNumber03 ||
          depObj?.patientMediKreditPlanNumber03,
        selected.mediKreditPlanNumber04 ||
          depObj?.patientMediKreditPlanNumber04,
        selected.mediKreditPlanNumber06 || depObj?.patientMediKreditPlanNumber06
      );

      return {
        ...medicalAidInfo,
        medicalAidPlan:
          selected.medicalAidPlan || medicalAidInfo.medicalAidPlan || "",
        medicalAidPlanCode:
          selected.medicalAidPlanCode ||
          medicalAidInfo.medicalAidPlanCode ||
          "",
        medicalAidOption:
          selected.medicalAidOption || medicalAidInfo.medicalAidOption || "",
        medicalAidOptionCode:
          selected.medicalAidOptionCode ||
          medicalAidInfo.medicalAidOptionCode ||
          "",
        ...mk,
        lastCheck: new Date().toISOString(),
        hnetNumber: depObj?.hnetNumber || "",
        txNumber: depObj?.txNumber || "",
      };
    },
    [depPlanSelections, medicalAidInfo, isIOD]
  );

  useEffect(() => {
    if (!importDepCode) return;

    const dep = dependants.find(
      (d) => String(d.dependantCode) === String(importDepCode)
    );
    if (!dep) return;

    const main =
      dependants.find(
        (d) => String(d.dependantCode) === String(mainMemberCode)
      ) || null;

    const depMedicalAidInfo = buildMedAidPayloadForDep(importDepCode, dep);
    const patientData = { ...dep, medicalAidInfo: depMedicalAidInfo };

    const mainMemberData = main
      ? {
          ...main,
          medicalAidInfo: buildMedAidPayloadForDep(mainMemberCode, main),
        }
      : null;

    if (typeof addPatientSteps === "function")
      addPatientSteps(patientData, mainMemberData);
    if (typeof onDependantSelect === "function") onDependantSelect(patientData);
  }, [
    importDepCode,
    mainMemberCode,
    dependants,
    depPlanSelections,
    addPatientSteps,
    onDependantSelect,
    buildMedAidPayloadForDep,
  ]);

  const buildPerson = useCallback(
    (depCode) => {
      const code = String(depCode || "");
      if (!code) return null;

      const dep = dependants.find((d) => String(d.dependantCode) === code);
      if (!dep) return null;

      return {
        ...dep,
        medicalAidInfo: buildMedAidPayloadForDep(code, dep),
      };
    },
    [dependants, buildMedAidPayloadForDep]
  );

  useEffect(() => {
    const patient = importDepCode ? buildPerson(importDepCode) : null;
    const mainMember = mainMemberCode ? buildPerson(mainMemberCode) : null;

    if (!patient && !mainMember) return;

    addPatientSteps?.(patient, mainMember);
  }, [importDepCode, mainMemberCode, buildPerson, addPatientSteps]);

  useEffect(() => {
    if (!mainMemberCode) return;

    const mainMemberData = buildPerson(mainMemberCode);
    if (!mainMemberData) return;

    addPatientSteps?.(null, mainMemberData);
  }, [mainMemberCode, buildPerson, addPatientSteps]);

  const commitIodToParent = useCallback(() => {
    console.log("🔍 Validating IOD...");
    const ok = validateIod();
    if (!ok) {
      console.log("❌ IOD validation failed:", iodErrors);
      return;
    }

    const payload = {
      iodFund: medicalAidInfo.iodFund || "",
      iodFundCode: medicalAidInfo.iodFundCode || "",
      iodPlan: iodPlanSelection.iodPlan || "",
      iodPlanCode: iodPlanSelection.iodPlanCode || "",
      iodOption: iodPlanSelection.iodOption || "",
      iodOptionCode: iodPlanSelection.iodOptionCode || "",
      mediKreditPlanNumber03: iodPlanSelection.mediKreditPlanNumber03 || "",
      mediKreditPlanNumber04: iodPlanSelection.mediKreditPlanNumber04 || "",
      mediKreditPlanNumber06: iodPlanSelection.mediKreditPlanNumber06 || "",
      plan: iodPlanSelection.plan || "",
      iodPatient: true,

      iodEmployer: iodCase?.iodEmployer || "",
      iodEmployerWCARegNum: iodCase?.employerRegistrationNumber || "",
      iodDateOfInjury: iodCase?.dateOfInjury || "",
      iodClaimRefNumber: iodCase?.claimRefNumber || "",

      lastCheck: new Date().toISOString(),
    };
    addPatientSteps?.(
      {
        iodInfo: payload,
        __iodCommitted: true,
        __iodCommittedAt: Date.now(),
      },
      null
    );

    // ✅ Set committed state to show success UI
    setIodCommitted(true);
    console.log("✅ IOD committed successfully:", payload);
  }, [
    validateIod,
    iodErrors,
    medicalAidInfo.iodFund,
    medicalAidInfo.iodFundCode,
    iodPlanSelection.iodPlan,
    iodPlanSelection.iodPlanCode,
    iodPlanSelection.iodOption,
    iodPlanSelection.iodOptionCode,
    iodPlanSelection.mediKreditPlanNumber03,
    iodPlanSelection.mediKreditPlanNumber04,
    iodPlanSelection.mediKreditPlanNumber06,
    iodPlanSelection.plan,
    iodCase.iodEmployer,
    iodCase.employerRegistrationNumber,
    iodCase.dateOfInjury,
    iodCase.claimRefNumber,
    addPatientSteps,
  ]);

  // ✅ Function to edit IOD again
  const handleEditIod = useCallback(() => {
    setIodCommitted(false);
  }, []);

  return (
    <>
      {/* ✅ Header inputs - hide when IOD is committed */}
      <Row className="mb-2">
        {(!accountExisting || editPatient || isIOD) && !(isIOD && iodCommitted) && (
          <>
            {/* --- Scheme / Fund --- */}
            <Col md={6}>
              <Form.Group controlId="formGridMedicalAid">
                <Form.Label>{isIOD ? "IOD Fund" : "Medical Aid"}</Form.Label>

                <SchemeSearch
                  category={isIOD ? "WCA" : undefined}
                  value={
                    isIOD ? medicalAidInfo.iodFund : medicalAidInfo.medicalAid
                  }
                  onSchemeCodeSelect={handleSchemeSelect}
                />
              </Form.Group>
            </Col>

            {/* --- Plan Option --- */}
            <Col md={6}>
              <Form.Group controlId="formGridPlanOption">
                <Form.Label>Plan Option</Form.Label>

                {isIOD && !medicalAidInfo.iodFundCode ? (
                  <div className="text-muted small">
                    Please select an IOD Fund first.
                  </div>
                ) : null}

                {!isIOD && !medicalAidInfo.medicalAidCode ? (
                  <div className="text-muted small">
                    Please select a Medical Aid first.
                  </div>
                ) : null}

                {isIOD && !!medicalAidInfo.iodFundCode && (
                  <PlanOptionSelector
                    schemeCode={medicalAidInfo.iodFundCode}
                    value={iodPlanSelection.selectedId || ""}
                    onChangeId={(id) => {
                      setIodCommitted(false); // ✅ Reset when changing plan
                      setIodPlanSelection((prev) => ({
                        ...prev,
                        selectedId: id,
                      }));
                    }}
                    onPlanOptionSelect={(data) => {
                      const mk = normalize030406(
                        data?.patientMediKreditPlanNumber03,
                        data?.patientMediKreditPlanNumber04,
                        data?.patientMediKreditPlanNumber06
                      );
                      setIodPlanSelection((prev) => ({
                        ...prev,
                        iodPlan: data?.patientMedicalAidPlan || "",
                        iodPlanCode: data?.patientMedicalAidPlanCode || "",
                        iodOption: data?.patientMedicalAidOption || "",
                        iodOptionCode: data?.patientMedicalAidOptionCode || "",
                        mediKreditPlanNumber03: mk.mediKreditPlanNumber03,
                        mediKreditPlanNumber04: mk.mediKreditPlanNumber04,
                        mediKreditPlanNumber06: mk.mediKreditPlanNumber06,
                        plan: mk.plan,
                      }));
                    }}
                  />
                )}

                {!isIOD && !!medicalAidInfo.medicalAidCode && (
                  <PlanOptionSelector
                    schemeCode={medicalAidInfo.medicalAidCode}
                    value={mainPlanSelection?.selectedId || ""}
                    onChangeId={(id) =>
                      setMainPlanSelection((prev) => ({
                        ...(prev || {}),
                        selectedId: id,
                      }))
                    }
                    onPlanOptionSelect={(data) => {
                      const mk = normalize030406(
                        data?.patientMediKreditPlanNumber03,
                        data?.patientMediKreditPlanNumber04,
                        data?.patientMediKreditPlanNumber06
                      );

                      setMainPlanSelection((prev) => ({
                        ...(prev || {}),
                        medicalAidPlan: data?.patientMedicalAidPlan || "",
                        medicalAidPlanCode:
                          data?.patientMedicalAidPlanCode || "",
                        medicalAidOption: data?.patientMedicalAidOption || "",
                        medicalAidOptionCode:
                          data?.patientMedicalAidOptionCode || "",
                        ...mk,
                      }));
                    }}
                  />
                )}
              </Form.Group>
            </Col>
            {isIOD && iodErrors.iodFundCode && (
              <div className="text-danger small mt-1">
                {iodErrors.iodFundCode}
              </div>
            )}
            {isIOD && iodErrors.iodPlan && (
              <div className="text-danger small mt-1">{iodErrors.iodPlan}</div>
            )}

            {!isIOD && (
              <Col md={6}>
                <Form.Group controlId="formGridMedicalAidNumber">
                  <Form.Label>Medical Aid Number</Form.Label>
                  <Form.Control
                    className="mb-2"
                    required
                    value={medicalAidInfo.ch_id}
                    name="ch_id"
                    onChange={handleNumberChange}
                  />
                </Form.Group>
              </Col>
            )}
          </>
        )}
      </Row>

      {/* ✅ IOD case details - show summary when committed */}
      {isIOD && (
        <>
          {/* ✅ Success Alert */}
          {iodCommitted && (
            <Alert variant="success" className="mb-3 d-flex align-items-center justify-content-between">
              <div>
                <i className="fa-solid fa-check-circle me-2"></i>
                <strong>IOD information updated successfully!</strong>
              </div>
              <Button
                variant="outline-success"
                size="sm"
                onClick={handleEditIod}
              >
                <i className="fa-solid fa-pen me-1"></i>
                Edit
              </Button>
            </Alert>
          )}

          {/* ✅ Summary View when committed */}
          {iodCommitted ? (
            <div className="border rounded p-3 mb-3 bg-light">
              <Row className="mb-2">
                <Col md={12}>
                  <h6 className="text-primary mb-3">
                    <i className="fa-solid fa-briefcase-medical me-2"></i>
                    IOD Case Summary
                  </h6>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={6}>
                  <div className="mb-2">
                    <small className="text-muted d-block">Fund</small>
                    <strong>{medicalAidInfo.iodFund}</strong>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-2">
                    <small className="text-muted d-block">Plan Option</small>
                    <strong>
                      {iodPlanSelection.iodOption} | {iodPlanSelection.iodPlan}
                    </strong>
                  </div>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={3}>
                  <div className="mb-2">
                    <small className="text-muted d-block">Claim Ref #</small>
                    <strong>{iodCase.claimRefNumber}</strong>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-2">
                    <small className="text-muted d-block">Date of Injury</small>
                    <strong>
                      {iodCase.dateOfInjury
                        ? new Date(iodCase.dateOfInjury).toLocaleDateString()
                        : ""}
                    </strong>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-2">
                    <small className="text-muted d-block">Employer</small>
                    <strong>{iodCase.iodEmployer}</strong>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-2">
                    <small className="text-muted d-block">Employer WCA Reg #</small>
                    <strong>{iodCase.employerRegistrationNumber}</strong>
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            /* ✅ Editable Form when NOT committed */
            <Row className="mb-3">
              <Col md={12}>
                <Row>
                  <Col md={3}>
                    <Form.Group controlId="formGridIODClaimRef">
                      <Form.Label>Claim Ref #</Form.Label>
                      <Form.Control
                        placeholder="Claim Reference Number"
                        name="claimRefNumber"
                        value={iodCase.claimRefNumber || ""}
                        onChange={handleChangeIodCase}
                        isInvalid={!!iodErrors.claimRefNumber}
                      />
                      <Form.Control.Feedback type="invalid">
                        {iodErrors.claimRefNumber}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group controlId="formGridIODDateOfInjury">
                      <Form.Label>Date of Injury</Form.Label>
                      <Form.Control
                        type="date"
                        name="dateOfInjury"
                        value={
                          iodCase.dateOfInjury
                            ? iodCase.dateOfInjury.slice(0, 10)
                            : ""
                        }
                        onChange={handleChangeIodCase}
                        isInvalid={!!iodErrors.dateOfInjury}
                      />
                      <Form.Control.Feedback type="invalid">
                        {iodErrors.dateOfInjury}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group controlId="formGridIODEmployer">
                      <Form.Label>Employer</Form.Label>
                      <Form.Control
                        placeholder="Employer"
                        name="iodEmployer"
                        value={iodCase.iodEmployer || ""}
                        onChange={handleChangeIodCase}
                        isInvalid={!!iodErrors.iodEmployer}
                      />
                      <Form.Control.Feedback type="invalid">
                        {iodErrors.iodEmployer}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group controlId="formGridIODEmployerReg">
                      <Form.Label>Employer WCA Reg #</Form.Label>
                      <Form.Control
                        placeholder="Employer Registration Number"
                        name="employerRegistrationNumber"
                        value={iodCase.employerRegistrationNumber || ""}
                        onChange={handleChangeIodCase}
                        isInvalid={!!iodErrors.employerRegistrationNumber}
                      />
                      <Form.Control.Feedback type="invalid">
                        {iodErrors.employerRegistrationNumber}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}

          {/* ✅ Update IOD button - only show when NOT committed */}
          {!iodCommitted && (
            <Row className="mb-2">
              <Col className="d-flex justify-content-end">
                <Button variant="primary" onClick={commitIodToParent}>
                  <i className="fa-solid fa-plus me-2"></i>
                  {editPatient?._id ? "Update IOD" : "Add IOD"}
                </Button>
              </Col>
            </Row>
          )}
        </>
      )}

      {/* ✅ Search (non-IOD only) */}
      {!isIOD && (
        <>
          <Row className="mb-3">
            <Button
              className="w-25 ms-3 d-flex align-items-center justify-content-center gap-2"
              onClick={famCheck}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  Loading...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-magnifying-glass"></i>
                  Search
                </>
              )}
            </Button>
          </Row>

          {/* Warning message display */}
          {warningMsg && (
            <Row className="mb-3">
              <Col>
                <div className="alert alert-warning py-2 mt-0 mb-0">
                  <i className="fa-solid fa-triangle-exclamation me-2"></i>
                  {warningMsg}
                </div>
              </Col>
            </Row>
          )}
        </>
      )}

      {/* Status info */}
      {!isIOD && statusInfo && (
        <Row className="mb-2">
          <Col>
            <div
              className={
                statusInfo?.resultCode === "A"
                  ? "text-success fw-bold"
                  : "text-danger fw-bold"
              }
            >
              {statusInfo?.resultCode === "A" ? "Success" : "Unsuccessful"}
            </div>

            {statusInfo?.warnings?.map((w, i) => (
              <div key={`w-${i}`}>
                {w.code} - {w.description}
              </div>
            ))}
            {statusInfo?.rejections?.map((r, i) => (
              <div key={`r-${i}`} className="text-danger">
                {r.code} - {r.description}
              </div>
            ))}
          </Col>
        </Row>
      )}

      {/* Apply searched info when FamilyCheck returns no dependants (works for both error and success with empty list) */}
      {!isIOD && dependants.length === 0 && statusInfo && (
        <Row className="mb-0">
          <Col>
            <Alert variant="warning" className="d-flex align-items-center justify-content-between py-2">
              <span>
                <i className="fa-solid fa-triangle-exclamation me-2"></i>
                No dependants returned. You can still apply the searched medical aid information. <br/><span className="fw-bold">NOTE:</span> Ensure the dependant code and main member selection are correct, it will be saved on the patient and the account. 
              </span>
              <Button
                variant={manualApplied ? "success" : "primary"}
                size="sm"
                disabled={manualApplied}
                onClick={() => {
                  const mk = normalize030406(
                    mainPlanSelection.mediKreditPlanNumber03 || medicalAidInfo.mediKreditPlanNumber03,
                    mainPlanSelection.mediKreditPlanNumber04 || medicalAidInfo.mediKreditPlanNumber04,
                    mainPlanSelection.mediKreditPlanNumber06 || medicalAidInfo.mediKreditPlanNumber06
                  );
                  const payload = {
                    ...medicalAidInfo,
                    medicalAidPlan: mainPlanSelection.medicalAidPlan || medicalAidInfo.medicalAidPlan || "",
                    medicalAidPlanCode: mainPlanSelection.medicalAidPlanCode || medicalAidInfo.medicalAidPlanCode || "",
                    medicalAidOption: mainPlanSelection.medicalAidOption || medicalAidInfo.medicalAidOption || "",
                    medicalAidOptionCode: mainPlanSelection.medicalAidOptionCode || medicalAidInfo.medicalAidOptionCode || "",
                    ...mk,
                    lastCheck: new Date().toISOString(),
                    txNumber: statusInfo?.txNumber || null,
                  };
                  const syntheticPatient = {
                    medicalAidInfo: payload,
                    dependantCode: "",
                    __manualApply: true,
                  };
                  addPatientSteps?.(syntheticPatient, syntheticPatient);
                  setManualApplied(true);
                }}
              >
                <i className={`fa-solid ${manualApplied ? "fa-circle-check" : "fa-check"} me-1`}></i>
                {manualApplied ? "Applied" : "Apply Searched Info"}
              </Button>
            </Alert>
          </Col>
        </Row>
      )}

      {/* ✅ Dependants table (non-IOD) */}
      {!isIOD && dependants.length > 0 && (
        <Row className="mb-3">
          <Col md={12}>
            <p>Please select the dependant and the main member:</p>

            <Table bordered hover size="sm" className="align-middle">
              <thead className="table-primary text-center fixed-table">
                <tr>
                  <th style={{ width: "10%" }}>Dep</th>
                  <th style={{ width: "30%" }}>Name</th>
                  <th style={{ width: "30%" }}>Plan</th>
                  <th style={{ width: "15%" }} className="text-center">
                    Main Mem
                  </th>
                  <th style={{ width: "15%" }} className="text-center">
                    Import
                  </th>
                </tr>
              </thead>

              <tbody>
                {dependants.map((dep) => {
                  const code = String(dep.dependantCode);

                  return (
                    <tr key={code}>
                      <td className="text-center">{code}</td>

                      <td>
                        {dep.dependantFirstName} {dep.dependantLastName}
                      </td>

                      <td>
                        <div>
                          <PlanOptionSelector
                            schemeCode={medicalAidInfo.medicalAidCode}
                            value={depPlanSelections[code]?.selectedId || ""}
                            onChangeId={(id) => {
                              setDepPlanSelections((prev) => ({
                                ...prev,
                                [code]: {
                                  ...(prev[code] || {}),
                                  selectedId: id,
                                },
                              }));
                            }}
                            onPlanOptionSelect={(data) => {
                              const mk = normalize030406(
                                data?.patientMediKreditPlanNumber03,
                                data?.patientMediKreditPlanNumber04,
                                data?.patientMediKreditPlanNumber06
                              );

                              setDepPlanSelections((prev) => ({
                                ...prev,
                                [code]: {
                                  ...(prev[code] || {}),
                                  medicalAidPlan:
                                    data?.patientMedicalAidPlan || "",
                                  medicalAidPlanCode:
                                    data?.patientMedicalAidPlanCode || "",
                                  medicalAidOption:
                                    data?.patientMedicalAidOption || "",
                                  medicalAidOptionCode:
                                    data?.patientMedicalAidOptionCode || "",
                                  ...mk,
                                },
                              }));
                            }}
                          />

                          <div className="text-muted small mt-1">
                            suggested plan is:{" "}
                            <strong>
                              {dep.dependantPlanDescription || "N/A"}
                            </strong>
                          </div>
                        </div>
                      </td>

                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={mainMemberCode === code}
                          onChange={() => handleMainMemberSelect(code)}
                        />
                      </td>

                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={importDepCode === code}
                          onChange={() => handleImportSelect(code)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <div className="text-muted small">
              * You can mark only one dependant to import. Main member can be
              changed.
            </div>
          </Col>
        </Row>
      )}
    </>
  );
}